import { Link } from "@tanstack/react-router"
import {
  ArrowRight,
  CalendarDays,
  Map,
  Share2,
  Users,
  Wallet,
  Wand2,
} from "lucide-react"
import { motion } from "motion/react"
import { Button } from "@/components/ui/button"
import { trackEvent } from "@/components/Analytics"

// The page exists to rank for "group trip planner" / "collaborative trip
// planner" — head terms our positioning owns but had zero surface for (2026-06-11
// SEO audit). Copy mirrors /ai-trip-planner: exact phrases in the H1/H2s, the
// substance is the real product (live shared editing); every claim maps to a
// shipped feature.

const steps = [
  {
    number: `01`,
    icon: Wand2,
    title: `Start the trip — your way`,
    body: `Create the trip from a blank slate, copy a guide, or ask Chic, your copilot, to draft a day-by-day starting point ("a long weekend in Barcelona for six friends"). The draft is just the opening move — the group does the rest.`,
  },
  {
    number: `02`,
    icon: Users,
    title: `Invite everyone who's going`,
    body: `Send your travel companions an invite and they join the same living itinerary as collaborators. No version numbers, no "final_plan_v3" — there is exactly one plan, and everyone is looking at it.`,
  },
  {
    number: `03`,
    icon: Map,
    title: `Plan together, live, on one map`,
    body: `Every edit — a dragged day, a swapped restaurant, a note on a museum — appears for the whole group in real time, with every stop pinned and routed on a shared map. Planning night becomes everyone editing at once instead of one person typing.`,
  },
]

const features = [
  {
    icon: Users,
    title: `Real-time collaborative editing`,
    body: `Everyone in the group edits the same itinerary at the same time, and changes sync live — closer to a shared doc than a forwarded plan.`,
  },
  {
    icon: Map,
    title: `One shared live map`,
    body: `Every place anyone adds lands on the same map, with routes and travel times between stops, so the whole group sees the shape of each day.`,
  },
  {
    icon: Wand2,
    title: `A copilot that drafts for the group`,
    body: `Chic, the built-in copilot, turns "5 days in Lisbon for 6 people, mixed budgets" into a real starting itinerary the group can immediately start pulling apart.`,
  },
  {
    icon: CalendarDays,
    title: `Drag-and-drop days for everyone`,
    body: `Any collaborator can reorder stops, move things between days, or shift whole days — and the map and routes keep up instantly.`,
  },
  {
    icon: Wallet,
    title: `Shared budgets and expenses`,
    body: `Set the trip budget and log expenses where everyone can see them, so money conversations happen during planning, not at the ATM.`,
  },
  {
    icon: Share2,
    title: `Share links and copyable guides`,
    body: `Send a read-only link to the family members who just want to know the plan, or publish the finished trip as a guide other travelers can copy.`,
  },
]

const faqs = [
  {
    q: `Is ChicTravel's group trip planner free?`,
    a: `Yes. Creating trips, AI itinerary drafts, the live map, and real-time collaboration with your whole group are all free, with no ads. Pro adds extras like the ChicReview trip audit and PDF/calendar export, but group planning itself never hits a paywall.`,
  },
  {
    q: `How many people can plan a trip together?`,
    a: `Invite your whole travel group — there's no practical cap for a normal trip. Everyone you invite becomes a collaborator on the same itinerary and can add places, edit days, and leave notes.`,
  },
  {
    q: `Do my friends need an account to see the plan?`,
    a: `No — anyone with the share link can view the full itinerary and map without signing up. To edit together, collaborators create a free account so every change is saved, attributed, and synced live to everyone else.`,
  },
  {
    q: `What happens when two people edit at the same time?`,
    a: `It just works — that's the point. ChicTravel syncs in real time, so when one person reorders day two while someone else adds a dinner spot, both changes appear for everyone within moments. There's no locking, no merging, and no "someone else has this open" warning.`,
  },
  {
    q: `How is this better than planning in a group chat?`,
    a: `The group chat is where ideas appear — and where they scroll away. A collaborative trip planner gives those ideas a permanent, structured home: every suggestion becomes a pinned place on a map, in a day, in an order. Keep the group chat for the jokes; keep the plan somewhere it can't be buried by them.`,
  },
  {
    q: `Can we still use AI if we're planning as a group?`,
    a: `Yes — as a copilot, which is the best version of it. Chic drafts the skeleton in seconds, then the group does what groups are good at: arguing taste, vetoing, and adding the one bar somebody swears by. You can also connect ChicTravel to Claude and make group edits from a chat window.`,
  },
]

function ctaClick(cta: string) {
  trackEvent(`cta_click`, { page: `group-trip-planner`, cta })
}

export function GroupTripPlannerPage() {
  // Same dual-render pattern as /claude: the FAQ copy is on the page for
  // readers and emitted as FAQPage JSON-LD for the rich-result eligibility.
  const faqJsonLd = {
    "@context": `https://schema.org`,
    "@type": `FAQPage`,
    mainEntity: faqs.map((f) => ({
      "@type": `Question`,
      name: f.q,
      acceptedAnswer: { "@type": `Answer`, text: f.a },
    })),
  }

  return (
    <main className="min-h-screen bg-background">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
      />

      {/* ── Hero ──────────────────────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-6 pt-12 pb-12 md:pt-16 md:pb-16">
        <div className="max-w-3xl mx-auto text-center flex flex-col items-center gap-5">
          <motion.div
            className="contents"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          >
            <span className="inline-flex items-center gap-2 w-fit rounded-full border border-border bg-card px-3 py-1 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              <Users className="h-3.5 w-3.5 text-primary" />
              Free for your whole group
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold leading-[1.08] tracking-tight">
              The group trip planner where
              <br />
              <span className="text-primary">everyone actually plans.</span>
            </h1>
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed max-w-xl">
              One living itinerary, one shared map, your whole crew editing it
              together in real time. No forwarded spreadsheets, no
              "final_plan_v3", no one person stuck doing all the work.
            </p>
            <div className="flex items-center gap-3 flex-wrap justify-center">
              <Button
                size="lg"
                className="rounded-full px-8 h-12 text-base font-semibold shadow-md gap-2"
                asChild
              >
                <Link to="/sign-up" onClick={() => ctaClick(`hero`)}>
                  Plan a group trip free <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="rounded-full px-8 h-12 text-base font-medium"
                asChild
              >
                <Link to="/trips/explore" onClick={() => ctaClick(`hero-explore`)}>
                  Browse real itineraries
                </Link>
              </Button>
            </div>
            <p className="text-muted-foreground text-xs">No credit card required</p>
          </motion.div>
        </div>
      </section>

      {/* ── How it works ──────────────────────────────────────────────── */}
      <section className="px-6 py-14 md:py-16 bg-card border-y border-border">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">
              How it works
            </p>
            <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight">
              Plan a trip together in three steps
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {steps.map((s, i) => (
              <motion.div
                key={s.number}
                className="rounded-2xl border border-border bg-background p-6 flex flex-col gap-3"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: `-60px` }}
                transition={{
                  duration: 0.5,
                  delay: i * 0.08,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                <div className="flex items-center gap-3">
                  <span className="text-3xl font-black text-primary/20 leading-none">
                    {s.number}
                  </span>
                  <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                    <s.icon className="h-5 w-5" />
                  </div>
                </div>
                <h3 className="font-bold text-lg">{s.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {s.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Why group trips fall apart ────────────────────────────────── */}
      <section className="px-6 py-16 md:py-20">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">
            The group chat problem
          </p>
          <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight mb-5">
            Group trips don't fail at the destination. They fail in the
            planning.
          </h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            You know the pattern. Someone proposes the trip and the group chat
            lights up — links, screenshots, "omg we have to do this" — and
            then it all scrolls away. Someone makes a spreadsheet nobody opens
            twice. Eventually one person becomes The Planner, does everything
            alone, and spends the trip fielding "wait, what's the plan today?"
          </p>
          <p className="text-muted-foreground leading-relaxed">
            A collaborative trip planner fixes the structural problem: the plan
            lives in{` `}
            <span className="font-semibold text-foreground">one place</span>
            {` `}everyone can edit, on{` `}
            <span className="font-semibold text-foreground">one map</span>
            {` `}everyone can see, updating{` `}
            <span className="font-semibold text-foreground">live</span> as the
            group shapes it. Ideas land in the itinerary instead of scrolling
            past it — and the work spreads across the people actually taking
            the trip.
          </p>
        </div>
      </section>

      {/* ── Feature grid ──────────────────────────────────────────────── */}
      <section className="px-6 py-14 md:py-16 bg-card border-y border-border">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">
              What's inside
            </p>
            <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight">
              Everything a collaborative trip planner needs
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((f, i) => (
              <motion.div
                key={f.title}
                className="rounded-2xl border border-border bg-background p-6 flex gap-4"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: `-60px` }}
                transition={{
                  duration: 0.5,
                  delay: (i % 3) * 0.08,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  <f.icon className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-bold text-lg mb-1">{f.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {f.body}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Built for every kind of group ─────────────────────────────── */}
      <section className="px-6 py-16 md:py-20">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">
              Who it's for
            </p>
            <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight">
              One planner, every kind of group trip
            </h2>
          </div>
          <div className="space-y-5 text-muted-foreground leading-relaxed">
            <p>
              <span className="font-semibold text-foreground">
                Friends' trips and reunions:
              </span>
              {` `}the classic case. Six people, six opinions, three budgets.
              Everyone adds their must-dos directly, the vetoes happen in
              planning instead of on the sidewalk, and nobody is The Planner
              by default.
            </p>
            <p>
              <span className="font-semibold text-foreground">
                Family vacations:
              </span>
              {` `}the organizers collaborate on the itinerary; the relatives
              who just want to know when dinner is get a read-only link. The
              shared map settles "how far is that from the hotel?" before
              anyone books anything.
            </p>
            <p>
              <span className="font-semibold text-foreground">
                Bachelor and bachelorette weekends:
              </span>
              {` `}high stakes, short timeline, one person traditionally
              drowning in logistics. Let Chic draft the weekend, split the
              days among the group, and keep the budget visible to everyone
              before the deposits go out.
            </p>
            <p>
              <span className="font-semibold text-foreground">
                Couples and travel partners:
              </span>
              {` `}the smallest group is still a group. Two people editing one
              itinerary together beats one person planning and the other
              discovering the plan at the airport.
            </p>
          </div>
        </div>
      </section>

      {/* ── FAQ ───────────────────────────────────────────────────────── */}
      <section className="px-6 py-14 md:py-16 bg-card border-y border-border">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">
              FAQ
            </p>
            <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight">
              Group trip planner questions, answered
            </h2>
          </div>
          <div className="flex flex-col gap-6">
            {faqs.map((f) => (
              <div
                key={f.q}
                className="rounded-2xl border border-border bg-background p-6"
              >
                <h3 className="font-bold text-base mb-2">{f.q}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {f.a}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ─────────────────────────────────────────────────── */}
      <section className="py-16 px-6 bg-primary">
        <motion.div
          className="max-w-2xl mx-auto text-center"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: `-60px` }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-primary-foreground mb-4">
            Plan the trip together. Take the trip together.
          </h2>
          <p className="text-primary-foreground/80 text-base mb-10">
            Start the itinerary, invite your crew, and watch the plan come
            together on one live map — free, for everyone going.
          </p>
          <div className="flex gap-3 justify-center flex-wrap">
            <Button
              size="lg"
              variant="secondary"
              className="rounded-full px-8 h-12 font-semibold shadow-md gap-2 bg-white text-primary hover:bg-white/90"
              asChild
            >
              <Link to="/sign-up" onClick={() => ctaClick(`footer`)}>
                Start planning free <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
          <p className="text-primary-foreground/60 text-xs mt-4">
            No credit card required
          </p>
        </motion.div>
      </section>
    </main>
  )
}
